/*
 * Phase 7 — Post-diagnostic services CTA card.
 *
 * Renders a tasteful "Need Help Fixing This?" card driven by the weakest
 * pillar in the active summary. Submitting opens a small modal where the
 * user picks a service category + writes a short message. The submission
 * POSTs to /api/service-inquiries and shows a confirmation.
 *
 * No proprietary remediation methodology is exposed — copy is intentionally
 * advisory and high-level.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { LifeBuoy, X, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getDiagHdrs = () => {
  try {
    const s = JSON.parse(localStorage.getItem("diag_session") || "null");
    if (!s?.user?.id || !s?.tenant?.id) return null;
    return { "x-tenant-id": s.tenant.id, "x-user-id": s.user.id, "x-user-role": s.user.role || "tenant_admin" };
  } catch { return null; }
};

// Map a pillar id → service category + recommended copy.
const PILLAR_SERVICE_MAP = {
  compliance: {
    category: "compliance_improvement",
    title: "Need help strengthening compliance readiness?",
    body: "Our team can help review missing evidence, build corrective action workflows, and prepare board-ready compliance tracking.",
  },
  student_perf: {
    category: "iep_special_ed_process",
    title: "Need help improving student outcomes & subgroup performance?",
    body: "Our education operations team can help assess subgroup gap drivers, intervention design, and data review cadence.",
  },
  governance: {
    category: "governance",
    title: "Need help strengthening board oversight?",
    body: "We can help build a board reporting cadence, governance dashboard, and corrective action tracking process.",
  },
  instruction: {
    category: "instructional_walkthrough",
    title: "Need help improving instructional visibility?",
    body: "We can help design walkthrough workflows, evidence collection, and instructional monitoring processes.",
  },
  fiscal: {
    category: "annual_report_readiness",
    title: "Need help improving fiscal visibility?",
    body: "We can help organize fiscal indicators, reporting cadence, and board-level financial risk visibility.",
  },
  admin_leadership: {
    category: "board_reporting",
    title: "Need help with leadership visibility & succession planning?",
    body: "Our advisory team can help structure leadership reporting and continuity readiness.",
  },
  cmo_emo: {
    category: "cmo_emo_evaluation",
    title: "Need help running a School Management evaluation?",
    body: "We can help structure your school management oversight framework and evaluation cadence.",
  },
  mission: {
    category: "board_reporting",
    title: "Need help re-aligning to mission & key design elements?",
    body: "Our team can support mission-alignment reviews, board re-grounding, and design-element documentation.",
  },
};

const CATEGORY_LABELS = {
  annual_report_readiness: "Annual Report Readiness",
  board_reporting: "Board Reporting",
  compliance_improvement: "Compliance Improvement",
  iep_special_ed_process: "IEP / Special Ed Process",
  governance: "Governance",
  cmo_emo_evaluation: "School Management Evaluation",
  instructional_walkthrough: "Instructional Walkthrough System",
  corrective_action_implementation: "Corrective Action Implementation",
  ai_workflow_optimization: "AI Workflow Optimization",
  custom: "Custom",
};

export const ServicesCTACard = ({ summary, diagnostic }) => {
  const [modal, setModal] = useState(false);

  const weakest = useMemo(() => {
    const cats = (summary?.pillar_summaries || summary?.category_summaries || []).filter((c) => c.health_score != null || c.score != null);
    return [...cats].sort((a, b) => ((a.health_score ?? a.score) - (b.health_score ?? b.score)))[0];
  }, [summary]);

  if (!weakest) return null;
  const pillarId = weakest.id;
  const cfg = PILLAR_SERVICE_MAP[pillarId] || PILLAR_SERVICE_MAP.compliance;

  return (
    <>
      <div className="mt-6 rounded-2xl border border-[#e5e7eb] bg-gradient-to-br from-[#0b1220] to-[#1e293b] text-white p-5 sm:p-6" data-testid="services-cta-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#007a9e]/20 text-[#00698a] flex items-center justify-center shrink-0">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#00698a] font-semibold">Need help fixing this?</div>
            <h3 className="text-lg font-bold mt-0.5">{cfg.title}</h3>
            <p className="text-[13px] text-[#cbd5e1] mt-2 leading-relaxed">{cfg.body}</p>
          </div>
          <Button
            onClick={() => setModal(true)}
            className="bg-[#007a9e] hover:bg-[#00607d] text-white shrink-0"
            data-testid="services-cta-open-btn"
          >
            Request Support
          </Button>
        </div>
      </div>
      {modal && (
        <InquiryModal
          defaultCategory={cfg.category}
          pillar={pillarId}
          riskArea={weakest.name || weakest.title || ""}
          diagnosticId={diagnostic?.id}
          onClose={() => setModal(false)}
        />
      )}
    </>
  );
};

const InquiryModal = ({ defaultCategory, pillar, riskArea, diagnosticId, onClose }) => {
  const [category, setCategory] = useState(defaultCategory || "custom");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (message.trim().length < 10) {
      toast.error("Please describe what you'd like help with (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/service-inquiries`, {
        inquiry_type: category,
        pillar,
        risk_area: riskArea,
        message,
        diagnostic_id: diagnosticId,
      }, { headers: getDiagHdrs() });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="services-cta-modal">
        <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#111827]">{done ? "Request received" : "Request Support"}</h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#111827]"><X className="h-5 w-5" /></button>
        </div>
        {done ? (
          <div className="p-6 text-center space-y-3">
            <div className="h-14 w-14 rounded-full bg-[#ecfdf5] text-[#059669] flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <p className="text-[14px] text-[#374151]">
              Thanks — your inquiry is on its way to our team. We'll follow up within two business days.
            </p>
            <Button onClick={onClose} className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white" data-testid="services-cta-modal-close-done">Close</Button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">Area of support</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="border border-[#e5e7eb] bg-white text-[#111827] text-[13px] rounded-md px-2 py-2 w-full"
                data-testid="services-cta-category-select"
              >
                {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">What would you like help with?</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A few sentences describing what's on your mind…"
                className="bg-white border-[#e5e7eb] text-[13px] min-h-[120px]"
                data-testid="services-cta-message"
              />
              <div className="text-[10px] text-[#9ca3af] mt-1">No proprietary methodology is shared with this request — our team responds privately.</div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="border-[#e5e7eb]">Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50"
                data-testid="services-cta-submit-btn"
              >
                {submitting ? "Sending…" : "Send Request"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesCTACard;
